/**
 * @author: Claude
 * @date: 2026/03/13
 * @desc: 右侧组件文档展示组件
 */
import React, { useRef, useMemo, useState, useEffect } from 'react';
import { Card, Icon } from 'tea-component';
import Loading from '@intelligence/components/Loading';
import { DEMO_MODULES, README_MODULES } from '../../config/demos';

import type { IComponentPreviewProps, IDemoCase } from './types';

import './index.scss';

// TODO: 改为从 DataBrain 的 cdn 上拉取
// highlight.js CDN 地址
const HIGHLIGHT_JS_CDN = 'https://cdn.jsdelivr.net/npm/highlight.js@11.9.0/lib/highlight.min.js';
const HIGHLIGHT_CSS_CDN = 'https://cdn.jsdelivr.net/npm/highlight.js@11.9.0/styles/github.min.css';

// 加载远程脚本
function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve();
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

// 加载远程样式
function loadStyle(href: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.onload = () => resolve();
    link.onerror = reject;
    document.head.appendChild(link);
  });
}

/**
 * 组件预览展示
 */
const ComponentPreview: React.FC<IComponentPreviewProps> = ({ componentInfo }) => {
  const [activeTab, setActiveTab] = useState('demo');
  const [DemoComponent, setDemoComponent] = useState<React.ComponentType | null>(null);
  const [demoConfig, setDemoConfig] = useState<IDemoCase[] | null>(null);
  const [demoLoading, setDemoLoading] = useState(false);
  const [readmeHtml, setReadmeHtml] = useState<string>('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [expandedCodeBlocks, setExpandedCodeBlocks] = useState<Set<number>>(new Set());
  const markdownRef = useRef<HTMLDivElement>(null);
  const demoCodeRefs = useRef<Map<number, HTMLPreElement>>(new Map());
  const highlightLoaded = useRef(false);

  // 加载 highlight.js 并高亮代码
  const highlightCodeBlocks = () => {
    const { hljs } = window as any;
    if (!hljs) return;

    // 高亮 Demo 代码块
    demoCodeRefs.current.forEach((pre) => {
      const code = pre.querySelector('code');
      if (code && !code.dataset.highlighted) {
        hljs.highlightElement(code);
        code.dataset.highlighted = 'true';
      }
    });
  };

  // 加载 highlight.js
  useEffect(() => {
    if (demoConfig && !highlightLoaded.current) {
      Promise.all([loadStyle(HIGHLIGHT_CSS_CDN), loadScript(HIGHLIGHT_JS_CDN)])
        .then(() => {
          highlightLoaded.current = true;
          highlightCodeBlocks();
        })
        .catch((err) => {
          console.warn('Failed to load highlight.js:', err);
        });
    } else if (demoConfig) {
      highlightCodeBlocks();
    }
  }, [demoConfig]);

  // 加载 Demo 组件
  useEffect(() => {
    // 切换组件时重置为 Demo 预览 tab
    setActiveTab('demo');

    if (componentInfo.hasDemo && DEMO_MODULES[componentInfo.path]) {
      setDemoLoading(true);
      setLoadError(null);
      setDemoConfig(null);
      setDemoComponent(null);
      demoCodeRefs.current.clear();
      setExpandedCodeBlocks(new Set()); // 切换组件时重置代码块展开状态

      DEMO_MODULES[componentInfo.path]()
        .then((module) => {
          // 检测是否为 demoConfig 格式
          if (Array.isArray(module.demoConfig)) {
            setDemoConfig(module.demoConfig);
          } else if (module.default) {
            // 检测默认导出是否为数组（demoConfig 格式）
            if (Array.isArray(module.default)) {
              setDemoConfig(module.default);
            } else {
              // 传统格式：React 组件
              setDemoComponent(() => module.default);
            }
          }
        })
        .catch((err) => {
          console.error('Failed to load demo:', err);
          setLoadError(err.message || '加载失败');
        })
        .finally(() => {
          setDemoLoading(false);
        });
    } else {
      setDemoComponent(null);
      setDemoConfig(null);
    }
  }, [componentInfo.path, componentInfo.hasDemo]);

  // 加载 README HTML 内容
  useEffect(() => {
    if (componentInfo.hasReadme && README_MODULES[componentInfo.path]) {
      README_MODULES[componentInfo.path]()
        .then((module) => {
          const html = module.default || module || '';
          setReadmeHtml(typeof html === 'string' ? html : '');
        })
        .catch(() => {
          setReadmeHtml('');
        });
    } else {
      setReadmeHtml('');
    }
  }, [componentInfo.path, componentInfo.hasReadme]);

  // Tab 配置
  const tabs = useMemo(() => {
    const result: Array<{ id: string; label: string }> = [{ id: 'demo', label: 'Demo 预览' }];

    if (readmeHtml) {
      result.push({ id: 'readme', label: '组件介绍' });
    }

    return result;
  }, [readmeHtml]);

  // 切换代码块展开/折叠
  const toggleCodeBlock = (index: number) => {
    setExpandedCodeBlocks((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // 渲染单个 Demo 案例
  const renderDemoCase = (demoCase: IDemoCase, index: number) => {
    const isCodeExpanded = expandedCodeBlocks.has(index);

    return (
      <div key={index} className="dbi-component-preview__demo-case">
        <div className="dbi-component-preview__case-header">
          <h4 className="dbi-component-preview__case-title">{demoCase.title}</h4>
          {demoCase.description && <p className="dbi-component-preview__case-desc">{demoCase.description}</p>}
        </div>
        <div className="dbi-component-preview__case-preview">{demoCase.element}</div>
        {/* 展开代码按钮 */}
        <div className="dbi-component-preview__code-toggle" onClick={() => toggleCodeBlock(index)}>
          <span className="dbi-component-preview__code-toggle-icon">
            <Icon type={isCodeExpanded ? 'arrow-up' : 'arrow-down'} />
          </span>
          <span className="dbi-component-preview__code-toggle-text">{isCodeExpanded ? '收起代码' : '展开代码'}</span>
        </div>
        {/* 代码块区域 - 带动画 */}
        <div
          className={`dbi-component-preview__case-code-wrapper ${
            isCodeExpanded ? 'dbi-component-preview__case-code-wrapper--expanded' : ''
          }`}
        >
          <div className="dbi-component-preview__case-code">
            <pre
              ref={(el) => {
                if (el) {
                  demoCodeRefs.current.set(index, el);
                  // 立即高亮（如果 highlight.js 已加载）
                  if (highlightLoaded.current) {
                    const code = el.querySelector('code');
                    if (code && !code.dataset.highlighted) {
                      const { hljs } = window as any;
                      if (hljs) {
                        hljs.highlightElement(code);
                        code.dataset.highlighted = 'true';
                      }
                    }
                  }
                }
              }}
            >
              <code className="language-tsx">{demoCase.code}</code>
            </pre>
          </div>
        </div>
      </div>
    );
  };

  // 渲染 Demo 预览内容
  const renderDemoContent = () => {
    if (!componentInfo.hasDemo) {
      return (
        <div className="dbi-component-preview__no-demo">
          <p>该组件暂无 Demo 文件</p>
          <p className="dbi-component-preview__hint">
            请在组件目录下创建 <code>demo.tsx</code> 文件
          </p>
        </div>
      );
    }

    if (demoLoading) {
      return (
        <div className="dbi-component-preview__loading">
          <Loading />
        </div>
      );
    }

    if (loadError) {
      return <div className="dbi-component-preview__empty-demo">Demo 加载失败: {loadError}</div>;
    }

    // 新格式：demoConfig 数组
    if (demoConfig) {
      return (
        <div className="dbi-component-preview__demo-cases">
          {demoConfig.map((demoCase, index) => renderDemoCase(demoCase, index))}
        </div>
      );
    }

    // 旧格式：React 组件
    if (DemoComponent) {
      return (
        <Card className="dbi-component-preview__demo-card">
          <Card.Header>实时预览</Card.Header>
          <Card.Body>
            <React.Suspense
              fallback={
                <div className="dbi-component-preview__loading">
                  <Loading />
                </div>
              }
            >
              <DemoComponent />
            </React.Suspense>
          </Card.Body>
        </Card>
      );
    }

    return <div className="dbi-component-preview__empty-demo">Demo 加载中...</div>;
  };

  // 渲染 Tab 内容
  const renderTabContent = () => {
    switch (activeTab) {
      case 'demo':
        return <div className="dbi-component-preview__demo-section">{renderDemoContent()}</div>;

      case 'readme':
        return (
          <div className="dbi-component-preview__readme-section">
            <div
              ref={markdownRef}
              className="markdown-body dbi-component-preview__markdown"
              dangerouslySetInnerHTML={{ __html: readmeHtml }}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="dbi-component-preview">
      {/* 组件标题 */}
      <div className="dbi-component-preview__header">
        <h1 className="dbi-component-preview__title">{componentInfo.name}</h1>
        {componentInfo.type === 'composite' && (
          <div className="dbi-component-preview__tags">
            <span className="dbi-component-preview__tag dbi-component-preview__tag--composite">复合组件</span>
          </div>
        )}
      </div>

      {/* 选项卡 - 自定义实现 */}
      <div className="dbi-component-preview__tabs">
        {tabs.map((tab) => (
          <div
            key={tab.id}
            className={`dbi-component-preview__tab ${activeTab === tab.id ? 'dbi-component-preview__tab--active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </div>
        ))}
      </div>

      {/* Tab 内容 */}
      <div className="dbi-component-preview__content">{renderTabContent()}</div>
    </div>
  );
};

export default ComponentPreview;
